import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import {
  CatalogCard,
  CatalogGrid,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { Cover } from '@/features/catalog/Cover';
import { isPageModel, recordPath, routePattern } from '@/features/catalog/pageModels';
import { sketchOf, type SketchKind } from '@/features/catalog/sketchOf';
import { useBlockTypes } from '@/features/catalog/useBlockTypes';
import { useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import { useViewMode } from '@/features/catalog/useViewMode';
import type { RecordBody, TypeBody } from '@/shared/api/generated/model';
import { recordsList } from '@/shared/api/generated/records/records';
import { useDebounced } from '@/shared/lib/useDebounced';
import { pluralize } from '@/shared/lib/plural';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';

import { NewRecordDialog } from '../ContentPage/NewRecordDialog';

const PER_PAGE = 48;
const BLOCKS = ['блок', 'блока', 'блоков'] as const;

/**
 * Папка в «Страницах»: записи модели-страниц — статьи, кейсы, услуги —
 * плитками с обложкой; клик — редактор записи. Записей бывает за сотню,
 * поэтому поиск — на сервере, а список — порциями.
 */
export function PageFolderPage() {
  const { siteId, env, model } = useParams({ from: '/authed/sites/$siteId/$env/pages/$model' });
  const types = useSchemaTypes(siteId, env);
  const type = (types.data ?? []).find((item) => item.slug === model && isPageModel(item));

  if (types.isPending) {
    return null;
  }

  if (type === undefined) {
    return (
      <div className={catalog['page']}>
        <CatalogHead title={<FolderTitle siteId={siteId} env={env} name="Нет такой папки" />} />
        <p className={catalog['empty']}>
          Папка — модель с адресом записи и полем из блоков. Модели «{model}» нет или она перестала
          быть страницами.
        </p>
      </div>
    );
  }

  return <Folder siteId={siteId} env={env} type={type} />;
}

function FolderTitle({ siteId, env, name }: { siteId: string; env: string; name: string }) {
  return (
    <>
      <Link to="/sites/$siteId/$env/pages" params={{ siteId, env } as never}>
        Страницы
      </Link>
      {' / '}
      {name}
    </>
  );
}

function Folder({ siteId, env, type }: { siteId: string; env: string; type: TypeBody }) {
  const navigate = useNavigate();
  const scope = useSiteScope();
  const [view, setView] = useViewMode('pages');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const needle = useDebounced(search.trim(), 300);
  const blocks = useBlockTypes(siteId, env as never);

  const records = useInfiniteQuery({
    queryKey: ['records', type.id, { folder: true, search: needle }],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await recordsList(type.id, {
        page: pageParam,
        per_page: PER_PAGE,
        ...(needle === '' ? {} : { search: needle }),
        ...(type.manual_order ? { sort: 'position', order: 'asc' as const } : {}),
      });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос записей');
      }

      return response.data;
    },
    getNextPageParam: (last) =>
      last.page * last.per_page < last.total ? last.page + 1 : undefined,
  });

  const list = records.data?.pages.flatMap((page) => page.items ?? []) ?? [];
  const total = records.data?.pages[0]?.total ?? 0;

  const blocksOf = (record: RecordBody) =>
    (record.data[type.blocks_field] ?? []) as { type?: string }[];
  const kindsOf = (record: RecordBody): SketchKind[] =>
    blocksOf(record).map((block) =>
      sketchOf(
        block.type ?? '',
        blocks.data?.find((item) => item.type === block.type)?.fields ?? [],
      ),
    );
  const coverOf = (record: RecordBody) => {
    const value = type.cover_field === '' ? undefined : record.data[type.cover_field];

    return typeof value === 'string' && value !== '' ? value : undefined;
  };
  const status = (record: RecordBody) =>
    record.status === 'published' ? (
      <Badge size="sm" tone="success">
        {record.has_unpublished_changes ? 'Есть правки' : 'На сайте'}
      </Badge>
    ) : (
      <Badge size="sm" tone="warning">
        Черновик
      </Badge>
    );
  const link = (record: RecordBody) => ({
    to: '/sites/$siteId/$env/r/$recordId',
    params: { siteId, env, recordId: record.id },
  });

  const canWrite = scope?.can('content.write') ?? false;

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={<FolderTitle siteId={siteId} env={env} name={type.name} />}
        subtitle={
          type.description !== ''
            ? type.description
            : `Страницы по адресу ${routePattern(type.route)} — записи модели «${type.name}».`
        }
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {canWrite && (
              <Button
                iconLeft={<Plus size={15} />}
                onClick={() => {
                  setCreating(true);
                }}
              >
                Новая запись
              </Button>
            )}
          </>
        }
      />

      <div className={catalog['bar']}>
        <CatalogSearch value={search} onChange={setSearch} placeholder="Поиск по названию" />
        <span className={catalog['num']}>
          {pluralize(total, ['страница', 'страницы', 'страниц'])}
        </span>
      </div>

      {view === 'grid' ? (
        <CatalogGrid>
          {list.map((record) => (
            <CatalogCard
              key={record.id}
              link={link(record)}
              preview={<Cover mediaId={coverOf(record)} kinds={kindsOf(record)} />}
              title={record.title === '' ? 'Без названия' : record.title}
              subtitle={recordPath(type.route, record)}
              badge={status(record)}
              meta={<span>{pluralize(blocksOf(record).length, BLOCKS)}</span>}
            />
          ))}
          {canWrite && needle === '' && !records.hasNextPage && (
            <NewCard
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus size={20} />
              Новая запись
            </NewCard>
          )}
        </CatalogGrid>
      ) : (
        <CatalogTable head={['Название', 'Адрес', 'Блоков', 'Статус']}>
          {list.map((record) => (
            <tr key={record.id}>
              <td>
                <Link
                  to="/sites/$siteId/$env/r/$recordId"
                  params={{ siteId, env, recordId: record.id } as never}
                >
                  {record.title === '' ? 'Без названия' : record.title}
                </Link>
              </td>
              <td className={catalog['mono']}>{recordPath(type.route, record)}</td>
              <td className={catalog['num']}>{blocksOf(record).length}</td>
              <td>{status(record)}</td>
            </tr>
          ))}
        </CatalogTable>
      )}

      {records.isSuccess && list.length === 0 && (
        <p className={catalog['empty']}>
          {needle === '' ? 'Здесь пока пусто.' : 'Ничего не нашлось.'}
        </p>
      )}

      {records.hasNextPage && (
        <div className={catalog['more']}>
          <Button
            variant="outline"
            loading={records.isFetchingNextPage}
            onClick={() => void records.fetchNextPage()}
          >
            Показать ещё
          </Button>
        </div>
      )}

      <NewRecordDialog
        open={creating}
        onOpenChange={setCreating}
        typeID={type.id}
        onDone={(record) => {
          void navigate(link(record) as never);
        }}
      />
    </div>
  );
}
